/**
 * features/finance/Documents.tsx
 * ------------------------------------------------------------------
 * EVERY PIECE OF EVIDENCE, AND EVERYTHING MISSING ONE.
 *
 * The attachment panel answers "what is behind this voucher?". This
 * answers the two questions the panel cannot, because both are about
 * the whole set rather than one record:
 *
 *   WHAT HAS BEEN PAID WITH NOTHING ON FILE. A paid voucher with no
 *   supporting document is money gone with nothing saying what for. It
 *   is the first thing an audit finds and the last thing anybody
 *   notices, because no single screen is missing anything — the gap only
 *   exists across the set.
 *
 *   WHERE IS THE DOCUMENT I AM LOOKING FOR. An auditor asks for the
 *   invoice behind PV/2026/0184 and the office needs to find it without
 *   knowing which screen it was attached from.
 *
 * REMOVED FILES ARE LISTED, not hidden. A file that was here and is not
 * any more is part of the trail; concealing it defeats the point of
 * keeping the record after the bytes are gone.
 * ------------------------------------------------------------------ */
import { useEffect, useMemo, useState } from 'react';
import { collection, onSnapshot, query } from 'firebase/firestore';
import {
  FolderOpen, FileText, Image as ImageIcon, AlertTriangle, ExternalLink,
  ShieldCheck,
} from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { db } from '@/lib/firebase';
import {
  ATTACHMENT_COLLECTION, ATTACHMENT_KIND_LABEL, EVIDENCE_LABEL, isLive, evidenceGaps,
  type FinanceAttachment, type AttachmentKind,
} from '@/lib/attachments';
import { VOUCHER_COLLECTIONS, type Voucher } from '@/lib/vouchers';
import {
  PageHeader, SoftCard, PanelHeader, StatChip, glassFieldClass,
} from '@/features/finance/ui';

const money = (n: number) =>
  n.toLocaleString('en-BW', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const size = (n: number) =>
  n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)}MB` : `${Math.round(n / 1024)}KB`;

export default function Documents() {
  const [rows, setRows] = useState<FinanceAttachment[] | null>(null);
  const [vouchers, setVouchers] = useState<Voucher[]>([]);
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState<AttachmentKind | ''>('');

  useEffect(() => onSnapshot(query(collection(db, ATTACHMENT_COLLECTION)),
    (s) => setRows(s.docs.map((d) => ({ id: d.id, ...d.data() }) as FinanceAttachment)),
    () => setRows([])), []);
  useEffect(() => onSnapshot(query(collection(db, VOUCHER_COLLECTIONS.vouchers)),
    (s) => setVouchers(s.docs.map((d) => ({ id: d.id, ...d.data() }) as Voucher)),
    () => setVouchers([])), []);

  const gaps = useMemo(
    () => evidenceGaps(vouchers, rows ?? []),
    [vouchers, rows],
  );

  const gapValue = useMemo(() => {
    const by = new Map(vouchers.map((v) => [v.id, Number(v.total) || 0]));
    return gaps.reduce((n, g) => n + (by.get(g.recordId) ?? 0), 0);
  }, [gaps, vouchers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (rows ?? [])
      .filter((a) => (kind ? a.kind === kind : true))
      .filter((a) => (q
        ? `${a.fileName} ${a.recordRef ?? ''} ${a.note ?? ''}`.toLowerCase().includes(q)
        : true))
      .sort((a, b) => (b.uploadedAt ?? '').localeCompare(a.uploadedAt ?? ''));
  }, [rows, kind, search]);

  if (rows === null) return <div className="p-6 flex justify-center"><Spinner /></div>;

  const live = rows.filter(isLive);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      <PageHeader
        icon={FolderOpen} tone="navy"
        title="Documents"
        subtitle="Every piece of evidence on file, and everything paid without any"
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        <StatChip tone="navy" icon={FolderOpen} value={String(live.length)} label="Documents on file" />
        <StatChip tone={gaps.length ? 'rose' : 'emerald'}
          icon={gaps.length ? AlertTriangle : ShieldCheck}
          value={String(gaps.length)} label="Paid with nothing on file" />
        <StatChip tone={gaps.length ? 'amber' : 'slate'}
          value={money(gapValue)} label="Value of those payments" />
      </div>

      {/* The finding no single screen can make, because nothing is missing
          from any one of them. */}
      {gaps.length > 0 && (
        <SoftCard className="mb-5 border-l-4 border-red-400">
          <p className="text-sm font-medium text-red-700 dark:text-red-300 mb-1">
            <AlertTriangle size={15} className="inline mr-1 -mt-0.5" />
            {gaps.length} payment{gaps.length === 1 ? ' has' : 's have'} been made with no
            supporting document — {money(gapValue)} in total.
          </p>
          <p className="text-[12px] text-gray-600 dark:text-gray-300 mb-2">
            The money has gone and nothing on file says what for. This is the first thing an
            audit finds, and the last thing anybody notices, because no single voucher screen
            is missing anything — the gap only exists across the set.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {gaps.slice(0, 15).map((g) => (
              <span key={g.recordId}
                className="text-[12px] font-mono px-2 py-0.5 rounded bg-red-50 text-red-800 dark:bg-red-950/40 dark:text-red-200">
                {g.recordRef}
              </span>
            ))}
            {gaps.length > 15 && (
              <span className="text-[12px] text-gray-500 px-1">
                and {gaps.length - 15} more
              </span>
            )}
          </div>
        </SoftCard>
      )}

      <SoftCard pad="p-0">
        <PanelHeader
          icon={FolderOpen} tone="navy" title="On file"
          subtitle="Newest first — removed files stay listed, because that is part of the trail"
          right={(
            <div className="flex items-center gap-2">
              <select value={kind} onChange={(e) => setKind(e.target.value as AttachmentKind | '')}
                className={glassFieldClass('max-w-[190px] text-xs')}>
                <option value="">Everything</option>
                {(Object.keys(ATTACHMENT_KIND_LABEL) as AttachmentKind[]).map((k) => (
                  <option key={k} value={k}>{ATTACHMENT_KIND_LABEL[k]}</option>
                ))}
              </select>
              <input value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="File or reference…"
                className={glassFieldClass('max-w-[190px] text-xs')} />
            </div>
          )}
        />

        {filtered.length === 0 ? (
          <p className="p-6 text-sm text-gray-500 text-center">
            {rows.length === 0
              ? 'Nothing has been attached yet. A payment with no supporting document is an assertion.'
              : 'Nothing matches that.'}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400 border-b border-gray-100 dark:border-gray-700/70">
                  <th className="py-2 pl-4 pr-2 font-semibold">File</th>
                  <th className="px-2 font-semibold">Attached to</th>
                  <th className="px-2 font-semibold">What it is</th>
                  <th className="px-2 font-semibold">Added</th>
                  <th className="px-2 pr-4 font-semibold text-right">Size</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((a) => (
                  <tr key={a.id} className={`border-b border-gray-50 dark:border-gray-800 ${
                    isLive(a) ? '' : 'opacity-60'
                  }`}>
                    <td className="py-2 pl-4 pr-2">
                      <span className="flex items-center gap-1.5">
                        {a.contentType === 'application/pdf'
                          ? <FileText size={14} className="text-gray-400 shrink-0" />
                          : <ImageIcon size={14} className="text-gray-400 shrink-0" />}
                        {isLive(a) && a.downloadUrl ? (
                          <a href={a.downloadUrl} target="_blank" rel="noopener noreferrer"
                            className="text-primaryLight hover:underline truncate max-w-[260px]">
                            {a.fileName}
                            <ExternalLink size={10} className="inline ml-1 -mt-0.5" />
                          </a>
                        ) : (
                          <s className="text-gray-500 truncate max-w-[260px]">{a.fileName}</s>
                        )}
                      </span>
                      {!isLive(a) && (
                        <span className="block text-[11px] text-gray-400 pl-5">
                          removed{a.supersededByName ? ` by ${a.supersededByName}` : ''}
                          {a.supersededReason ? `: ${a.supersededReason}` : ''}
                        </span>
                      )}
                    </td>
                    <td className="px-2">
                      <span className="font-mono text-xs text-gray-600 dark:text-gray-300">
                        {a.recordRef ?? a.recordId}
                      </span>
                      <span className="block text-[11px] text-gray-400">
                        {ATTACHMENT_KIND_LABEL[a.kind]}
                      </span>
                    </td>
                    <td className="px-2 text-gray-600 dark:text-gray-300">
                      {EVIDENCE_LABEL[a.evidence]}
                      {a.note && <span className="block text-[11px] text-gray-400">{a.note}</span>}
                    </td>
                    <td className="px-2 text-gray-500 text-xs">
                      {(a.uploadedAt ?? '').slice(0, 10)}
                      {a.uploadedByName && (
                        <span className="block text-[11px] text-gray-400">{a.uploadedByName}</span>
                      )}
                    </td>
                    <td className="px-2 pr-4 text-right tabular-nums text-gray-500">
                      {size(a.sizeBytes)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SoftCard>
    </div>
  );
}
